import React, { useEffect, useMemo, useState } from "react";
import {
  ClipboardCheck,
  ExternalLink,
  FileSignature,
  FileText,
  LoaderPinwheel,
  Search,
  Undo2,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { Button } from "../ui/button";
import FirmaCanvas from "../ui/FirmaCanvas";
import DevolverCalibracionModal from "./DevolverCalibracionModal";
import { verReporteIndividual } from "../../utils/reports";
import {
  capturarMetadatosFirma,
  filtrarElegibles,
  firmarRevisiones,
  listarRevisionesPendientes,
  obtenerFirmaRegistrada,
} from "../../utils/calibracionRevision";

const RESULTADO_BADGE = {
  Conforme: "bg-green-50 text-green-700 border border-green-200",
  "No conforme": "bg-red-50 text-red-700 border border-red-200",
};

const fechaCorta = (fecha) =>
  fecha ? new Date(fecha).toLocaleDateString("es-CO") : "—";

// Revisión y firma en bloque de las calibraciones asignadas al usuario.
const FirmarRevisionModal = ({ onClose, onCambio }) => {
  const [pendientes, setPendientes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [seleccion, setSeleccion] = useState(() => new Set());
  const [firma, setFirma] = useState("");
  const [firmando, setFirmando] = useState(false);
  const [devolviendo, setDevolviendo] = useState(null);
  const [abriendoPdf, setAbriendoPdf] = useState(null);

  const [firmaRegistrada, setFirmaRegistrada] = useState(null);

  const cargar = async () => {
    setCargando(true);
    try {
      setPendientes(await listarRevisionesPendientes());
    } catch {
      toast.error("Error al cargar las revisiones pendientes");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    obtenerFirmaRegistrada()
      .then(setFirmaRegistrada)
      .catch(() => {});
  }, []);

  const visibles = useMemo(
    () => filtrarElegibles(pendientes, { busqueda }),
    [pendientes, busqueda],
  );
  const todasMarcadas =
    visibles.length > 0 && visibles.every((s) => seleccion.has(s._id));

  const alternar = (id) =>
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const alternarTodas = () =>
    setSeleccion((prev) => {
      const next = new Set(prev);
      visibles.forEach((s) => (todasMarcadas ? next.delete(s._id) : next.add(s._id)));
      return next;
    });

  const verPdf = async (id) => {
    setAbriendoPdf(id);
    await verReporteIndividual(id);
    setAbriendoPdf(null);
  };

  const firmar = async () => {
    if (!seleccion.size) {
      toast.info("Selecciona al menos una calibración");
      return;
    }
    if (!firma) {
      toast.warning("Captura tu firma antes de continuar");
      return;
    }
    setFirmando(true);
    try {
      const metadatos = await capturarMetadatosFirma();
      const { message, resultados } = await firmarRevisiones({
        cronogramaIds: [...seleccion],
        firma,
        ...metadatos,
      });
      const fallidas = resultados.filter((r) => !r.ok);
      toast.success(message);
      if (fallidas.length) {
        toast.warning(
          `${fallidas.length} no se pudo(pudieron) firmar: ${fallidas[0].motivo}`,
        );
      }
      setSeleccion(new Set(fallidas.map((r) => r.id)));
      setFirma("");
      onCambio?.();
      await cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al registrar la firma");
    } finally {
      setFirmando(false);
    }
  };

  const alDevolver = (id) => {
    setDevolviendo(null);
    setSeleccion((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    onCambio?.();
    cargar();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-2 backdrop-blur-sm sm:p-4">
      <div className="flex max-h-[95vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-4 sm:px-6">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-violet-600 p-2">
              <ClipboardCheck className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Calibraciones para revisión</h2>
              <p className="text-xs text-gray-500">
                Revisa cada reporte y firma las seleccionadas con una sola firma.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={firmando}
            aria-label="Cerrar"
            className="text-gray-400 transition-colors hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto p-4 sm:p-6">
          {cargando ? (
            <div className="py-14 text-center text-sm text-gray-400">
              <LoaderPinwheel className="mx-auto mb-2 h-6 w-6 animate-spin text-blue-400" />
              Cargando revisiones...
            </div>
          ) : pendientes.length === 0 ? (
            <div className="py-14 text-center">
              <ClipboardCheck className="mx-auto mb-3 h-10 w-10 text-gray-200" />
              <p className="font-medium text-gray-500">No tienes revisiones pendientes</p>
              <p className="mt-1 text-xs text-gray-400">
                Las calibraciones aparecen aquí cuando el metrólogo las finaliza.
              </p>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="relative w-full sm:max-w-xs">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    placeholder="Serial, marca, modelo o cliente…"
                    className="w-full rounded-lg border py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
                <span className="text-xs font-medium text-gray-500">
                  {seleccion.size} de {pendientes.length} seleccionada(s)
                </span>
              </div>

              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100 text-xs uppercase tracking-wide text-gray-600">
                      <th className="w-10 px-4 py-3">
                        <input
                          type="checkbox"
                          aria-label="Seleccionar todas"
                          className="cursor-pointer rounded border-gray-300"
                          checked={todasMarcadas}
                          onChange={alternarTodas}
                        />
                      </th>
                      <th className="px-4 py-3 text-left">Equipo</th>
                      <th className="px-4 py-3 text-left">Cliente / Sede</th>
                      <th className="px-4 py-3 text-left">Metrólogo</th>
                      <th className="px-4 py-3 text-left">Calibración</th>
                      <th className="px-4 py-3 text-left">Resultado</th>
                      <th className="px-4 py-3 text-right">Revisión</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {visibles.map((s) => (
                      <tr key={s._id} className="transition-colors hover:bg-gray-50">
                        <td className="px-4 py-2.5 text-center">
                          <input
                            type="checkbox"
                            aria-label={`Seleccionar ${s.equipo?.serial || s._id}`}
                            className="cursor-pointer rounded border-gray-300"
                            checked={seleccion.has(s._id)}
                            onChange={() => alternar(s._id)}
                          />
                        </td>
                        <td className="px-4 py-2.5">
                          <p className="text-xs font-medium text-gray-800">
                            {s.equipo?.marca?.name || "—"} {s.equipo?.modelo?.name || ""}
                          </p>
                          <p className="mt-0.5 font-mono text-xs text-gray-400">
                            {s.equipo?.serial || "—"}
                          </p>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-600">
                          <p className="font-medium text-gray-700">
                            {s.equipo?.cliente?.name || "—"}
                          </p>
                          <p className="text-gray-400">{s.equipo?.sede?.name || "—"}</p>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-600">
                          {s.metrologo?.name || "—"}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-xs text-gray-600">
                          {fechaCorta(s.fechaEfectiva)}
                        </td>
                        <td className="px-4 py-2.5">
                          <span
                            className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${
                              RESULTADO_BADGE[s.gestion?.resultadoGlobal] ||
                              "border border-amber-200 bg-amber-50 text-amber-700"
                            }`}
                          >
                            {s.gestion?.resultadoGlobal || "Sin resultado"}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => verPdf(s._id)}
                              disabled={abriendoPdf === s._id}
                              className="flex items-center gap-1 whitespace-nowrap rounded-lg border border-violet-200 bg-violet-50 px-2.5 py-1.5 text-xs font-medium text-violet-700 transition-colors hover:bg-violet-100 disabled:opacity-60"
                            >
                              <FileText className="h-3 w-3" />
                              {abriendoPdf === s._id ? "Abriendo..." : "Reporte"}
                            </button>
                            <button
                              type="button"
                              onClick={() => window.open(`/manage/${s._id}`, "_blank")}
                              className="flex items-center gap-1 whitespace-nowrap rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50"
                            >
                              <ExternalLink className="h-3 w-3" />
                              Formulario
                            </button>
                            <button
                              type="button"
                              onClick={() => setDevolviendo(s)}
                              className="flex items-center gap-1 whitespace-nowrap rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-100"
                            >
                              <Undo2 className="h-3 w-3" />
                              Devolver
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {visibles.length === 0 && (
                      <tr>
                        <td colSpan={7} className="py-10 text-center text-sm text-gray-400">
                          No se encontraron resultados
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="space-y-4 border-t border-gray-100 pt-5">
                <FirmaCanvas
                  label="Firma de revisión"
                  value={firma}
                  onChange={setFirma}
                  firmaRegistrada={firmaRegistrada}
                  disabled={firmando}
                />
                <p className="text-xs text-gray-500">
                  Al firmar se registran tu nombre, tu rol, la fecha, la ubicación
                  aproximada, la IP ofuscada y el dispositivo, igual que en la
                  autorización de servicios.
                </p>
              </div>
            </>
          )}
        </div>

        {pendientes.length > 0 && !cargando && (
          <div className="flex flex-col-reverse gap-2 border-t border-gray-100 px-4 py-3 sm:flex-row sm:justify-end sm:px-6">
            <Button type="button" variant="outline" onClick={onClose} disabled={firmando}>
              Cerrar
            </Button>
            <Button
              type="button"
              className="bg-green-600 hover:bg-green-700"
              onClick={firmar}
              disabled={firmando || !seleccion.size || !firma}
            >
              <FileSignature size={14} className="mr-1.5" />
              {firmando ? "Firmando..." : `Firmar seleccionadas (${seleccion.size})`}
            </Button>
          </div>
        )}
      </div>

      <DevolverCalibracionModal
        servicio={devolviendo}
        onClose={() => setDevolviendo(null)}
        onDevuelta={alDevolver}
      />
    </div>
  );
};

export default FirmarRevisionModal;
